import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { merge, timer } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { CryptoService } from 'src/app/core/api/crypto-js/crypto.service';
import { AlertHistoryRow } from 'src/app/shared/components/alert-history-panel/alert-history-panel.component';

@Component({
  selector: 'app-basic-layout',
  template: `
    <div id="app-container">
      <div class="header-container">
        <div class="header-left">
          <button
            *ngIf="showTimestudyBackBtn"
            type="button"
            class="header-back-btn"
            (click)="goBack()"
            aria-label="戻る"
          >
            <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
              />
            </svg>
          </button>
          <div *ngIf="!showTimestudyBackBtn" class="header-spacer" aria-hidden="true"></div>
        </div>
        <div #headerTitle class="title">{{ title }}</div>
        <div class="header-actions">
          <button
            *ngIf="showAlertHistoryBtn"
            type="button"
            class="header-alert-icon-btn"
            (click)="openAlertHistory()"
            aria-label="アラート履歴"
          >
            <span class="header-notify-wrap">
              <img src="assets/images/icon/notify.svg" alt="" />
              <span *ngIf="hasUnreadAlerts" class="header-notify-dot" aria-hidden="true"></span>
            </span>
          </button>
        </div>
      </div>
      <router-outlet></router-outlet>
      <app-bottom-nav class="app-bottom-nav"></app-bottom-nav>
      <app-alert-history-panel
        [open]="alertHistoryOpen"
        [items]="alertHistoryItems"
        (closed)="onAlertHistoryClosed()"
        (markAsRead)="onAlertMarkAsRead($event)"
      ></app-alert-history-panel>
    </div>
  `,
  styles: [
    `
    #app-container {
      height: 100%;
      width: 100%;
      padding:0;
      margin:0;
      background-color: white;
    }
    .header-container {
      display: grid;
      grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
      align-items: center;
      background-color: #8259A5;
      /* 高さをpadding込みで制御（大きすぎるヘッダーを防ぐ） */
      padding: 0.65rem 0.5rem;
      border-bottom-left-radius: 2.5rem;
      border-bottom-right-radius: 2.5rem;
      /* フォント自動縮小しても上下が切れないよう少しだけ余裕を持たせる */
      height: 4.25rem;
      box-sizing: border-box;
    }
    .header-left {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      min-width: 0;
    }
    .header-spacer {
      width: 100%;
      min-height: 2.25rem;
    }
    .header-back-btn {
      width: 2.25rem;
      height: 2.25rem;
      border: none;
      border-radius: 0.5rem;
      background: rgba(255, 255, 255, 0.22);
      color: #ffffff;
      padding: 0;
      margin: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      flex-shrink: 0;
      transition: background-color 0.15s ease, transform 0.12s ease;
    }
    .header-back-btn:hover {
      background: rgba(255, 255, 255, 0.32);
    }
    .header-back-btn:active {
      transform: scale(0.96);
    }
    .header-container .title {
      color: white;
      /* 1行表示：長い場合は TS 側で自動縮小 */
      font-size: 1.5rem;
      font-weight: 600;
      text-align: center;
      margin: 0;
      min-width: 0;
      display: block;
      /* 日本語フォントの上下はみ出しで切れやすいので行高と余白を固定 */
      line-height: 1.05;
      padding: 0.1rem 0;
      /* 省略記号は出さず、1行に収める */
      white-space: nowrap;
      overflow: hidden;
      text-overflow: clip;
    }
    .header-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
    .header-alert-icon-btn {
      border: none;
      background: transparent;
      padding: 0.25rem;
      margin: 0;
      cursor: pointer;
      line-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .header-notify-wrap {
      position: relative;
      display: inline-block;
      line-height: 0;
    }
    .header-alert-icon-btn img {
      width: 1.5rem;
      height: 1.5rem;
      display: block;
      vertical-align: middle;
    }
    .header-notify-dot {
      position: absolute;
      top: 0;
      right: 0;
      width: 0.45rem;
      height: 0.45rem;
      background: #ff4d4f;
      border-radius: 50%;
      box-sizing: border-box;
      border: 1px solid #8259a5;
      transform: translate(15%, -15%);
    }
    .app-bottom-nav {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 4rem;
      z-index: 1000;
    }
    @media screen and (max-height: 460px) {
      .app-bottom-nav {
        display: none;
      }
    }
    `,
  ],
})
export class BasicLayoutComponent implements OnInit {
  title = 'ホーム';
  /** タイムスタディ画面のみヘッダー左に戻るを表示 */
  showTimestudyBackBtn = false;
  showAlertHistoryBtn = false;
  /** 未読アラートがあるときヘッダー notify アイコンに赤点を表示 */
  hasUnreadAlerts = false;

  alertHistoryOpen = false;
  alertHistoryItems: AlertHistoryRow[] = [];

  @ViewChild('headerTitle', { static: true }) headerTitleEl!: ElementRef<HTMLElement>;

  /** /profile 返却の userName（表示用） */
  private profileUserName: string | null = null;
  private profileKey: string | null = null;
  private profileFetchInFlight = false;

  private readonly alertListRequestBody = {
    alertType: 'VITAL_RISK',
    timeRange: 'today',
    isRead: null as number | null,
  };

  constructor(
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private crypto: CryptoService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
  ) {}

  goBack(): void {
    this.location.back();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.fitHeaderTitle();
  }

  ngOnInit() {
    merge(
      timer(0),
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
    )
      .pipe(
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap(route => route.data.pipe(take(1))),
      )
      .subscribe(data => {
        const path = this.router.url.split('?')[0];
        const routeTitle = data['title'] || '';
        const info = this.crypto.get('_s');

        const isPatientList = path === '/patient/new-list' || path === '/patient';
        const isTimestudy = path === '/patient/timestudy';

        this.showAlertHistoryBtn = isPatientList;
        this.showTimestudyBackBtn = isTimestudy;

        // 患者一覧・タイムスタディ：タイトルは /profile の userName を表示（入力値は使わない）
        if (isPatientList || isTimestudy) {
          this.ensureProfileUserName();
          this.title = this.profileUserName != null ? this.profileUserName : '';
        } else {
          this.title = routeTitle;
        }
        this.fitHeaderTitle();

        if (isPatientList) {
          this.refreshUnreadBadge();
        } else {
          this.hasUnreadAlerts = false;
        }
      });
  }

  private fitHeaderTitle(): void {
    // 1行・省略なしの要件：DOM計測でフォントサイズを自動調整する
    const el = this.headerTitleEl?.nativeElement;
    if (!el) return;

    // 初期化：最大サイズに戻してから測る
    const maxPx = 24; // 1.5rem 相当（基準16px）
    const minPx = 12; // 下限（読みやすさ確保）
    el.style.fontSize = `${maxPx}px`;

    // 次フレームでレイアウト確定後に計測
    requestAnimationFrame(() => {
      const container = el.parentElement as HTMLElement | null;
      if (!container) return;

      const available = container.clientWidth;
      if (available <= 0) return;

      // 収まっていればそのまま
      if (el.scrollWidth <= available) {
        return;
      }

      // 二分探索で「収まる最大フォントサイズ」を探す
      let lo = minPx;
      let hi = maxPx;
      for (let i = 0; i < 10; i++) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth <= available) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      el.style.fontSize = `${lo}px`;
    });
  }

  private ensureProfileUserName(): void {
    const info = this.crypto.get('_s');
    const hospitalId = info?.hospitalId != null ? String(info.hospitalId) : '';
    const username = info?.username != null ? String(info.username) : '';
    const key = `${hospitalId}|${username}`;

    if (!hospitalId || !username) {
      this.profileUserName = null;
      this.profileKey = null;
      return;
    }

    if (this.profileKey === key && this.profileUserName != null) {
      return;
    }
    if (this.profileFetchInFlight && this.profileKey === key) {
      return;
    }

    this.profileFetchInFlight = true;
    this.profileKey = key;

    const body = JSON.stringify({ username, hospitalId });
    this.http.post<any>('/profile', body).subscribe({
      next: (res) => {
        if (this.profileKey !== key) {
          return;
        }
        if (res?.resultCode === 200 && res.data?.userName != null) {
          const n = String(res.data.userName).trim();
          this.profileUserName = n.length > 0 ? n : null;
        } else {
          this.profileUserName = null;
        }
        this.profileFetchInFlight = false;
        // 現在表示中のページが対象ならタイトルを更新
        const path = this.router.url.split('?')[0];
        const isPatientList = path === '/patient/new-list' || path === '/patient';
        const isTimestudy = path === '/patient/timestudy';
        if (isPatientList || isTimestudy) {
          this.title = this.profileUserName != null ? this.profileUserName : '';
          this.cdr.markForCheck();
          this.fitHeaderTitle();
        }
      },
      error: () => {
        if (this.profileKey === key) {
          this.profileUserName = null;
        }
        this.profileFetchInFlight = false;
        this.cdr.markForCheck();
        this.fitHeaderTitle();
      },
    });
  }

  openAlertHistory(): void {
    this.http.post<any>('/alert/list', this.alertListRequestBody).subscribe({
      next: (res) => {
        if (res?.resultCode === 200 && Array.isArray(res.data?.list)) {
          const sortedList = [...res.data.list].sort((a: any, b: any) => {
            const levelOrder: { [key: string]: number } = { CRITICAL: 0, HIGH: 1 };
            return (levelOrder[a.alertLevel] ?? 999) - (levelOrder[b.alertLevel] ?? 999);
          });
          this.alertHistoryItems = sortedList.map((x: any) => ({
            alertHistoryId: x.alertHistoryId,
            patientName: x.patientName != null ? String(x.patientName) : '',
            alertTime: this.formatAlertTimeDisplay(x.alertTime != null ? String(x.alertTime) : ''),
            isRead: Number(x.isRead) === 1,
          }));
          this.hasUnreadAlerts = this.alertHistoryItems.some((r) => !r.isRead);
        } else {
          this.alertHistoryItems = [];
          this.hasUnreadAlerts = false;
        }
        this.alertHistoryOpen = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertHistoryItems = [];
        this.hasUnreadAlerts = false;
        this.alertHistoryOpen = true;
        this.cdr.markForCheck();
      },
    });
  }

  private refreshUnreadBadge(): void {
    this.http.post<any>('/alert/list', this.alertListRequestBody).subscribe({
      next: (res) => {
        if (res?.resultCode === 200 && Array.isArray(res.data?.list)) {
          this.hasUnreadAlerts = res.data.list.some((x: any) => Number(x.isRead) !== 1);
        } else {
          this.hasUnreadAlerts = false;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.hasUnreadAlerts = false;
        this.cdr.markForCheck();
      },
    });
  }

  onAlertHistoryClosed(): void {
    this.alertHistoryOpen = false;
  }

  /** POST /alert/markAsRead — 未読行のみ（既読は呼ばない） */
  onAlertMarkAsRead(alertHistoryId: number): void {
    const row = this.alertHistoryItems.find((r) => r.alertHistoryId === alertHistoryId);
    if (!row || row.isRead === true) {
      return;
    }

    this.http.post<any>('/alert/markAsRead', { alertHistoryId }).subscribe({
      next: (res) => {
        if (res?.resultCode === 200) {
          row.isRead = true;
          this.hasUnreadAlerts = this.alertHistoryItems.some((r) => !r.isRead);
          this.cdr.markForCheck();
        }
      },
      error: () => {},
    });
  }

  private formatAlertTimeDisplay(s: string): string {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}:\d{2}:\d{2})/);
    if (m) {
      return `${m[1]}/${m[2]}/${m[3]} ${m[4]}`;
    }
    return s;
  }
}
